import React, { Component } from "react"
import * as mapboxgl from "mapbox-gl"
import settings from "settings"
import axios from "axios"
import { toast } from "react-toastify"
import moment from "moment"
import NumberFormat from "react-number-format"
import { SingleDatePicker } from "react-dates"
import "mapbox-gl/dist/mapbox-gl.css"

mapboxgl.accessToken = settings.mapboxKey

class DriveMode extends Component {
	constructor(props) {
		super(props)
		this.mapContainer = React.createRef()
		this.state = {
			userLocation: null,
			selectedLot: null,
			showLotForm: false,
			start_date: null,
			complete_date: null,
			start_dateFocused: false,
			complete_dateFocused: false,
			loading: false,
			lots: [],
			propertyCenter: null,
			showMarkingOptions: false,
			markedLots: this.getMarkedLotsFromStorage(),
			statusMode: "construction", // 'construction' or 'sale'
			showMarkedLotsPanel: false, // for collapsible marked lots panel
			isAnimating: false, // for exit animation
			isVisible: false, // for entry animation
			showEmailConfirmModal: false, // for email builders confirmation
			isFollowingUser: true, // whether map should follow user location
			showStickToMeButton: false, // show button to re-enable following
		}
	}

	componentDidMount() {
		// Trigger entry animation
		setTimeout(() => {
			this.setState({ isVisible: true })
		}, 50) // Small delay to ensure proper animation

		this.loadPropertyCenterAndInitialize()
	}

	getMarkedLotsFromStorage = () => {
		const stored = localStorage.getItem("driveMode_markedLots")
		if (!stored) return []

		try {
			const markedLots = JSON.parse(stored)
			// Filter out any malformed entries that don't have required properties
			return markedLots.filter((mark) => mark.lotId && mark.markType)
		} catch (error) {
			console.error("Error parsing marked lots from storage:", error)
			// Clear corrupted data
			localStorage.removeItem("driveMode_markedLots")
			return []
		}
	}

	saveMarkedLotsToStorage = (markedLots) => {
		localStorage.setItem("driveMode_markedLots", JSON.stringify(markedLots))
	}

	clearMarkedLots = () => {
		if (
			window.confirm(
				`Are you sure you want to clear all ${this.state.markedLots.length} marked lots? This action cannot be undone.`
			)
		) {
			localStorage.removeItem("driveMode_markedLots")
			this.setState({ markedLots: [] })
			toast.success("All marked lots cleared", { autoClose: 2000 })
		}
	}

	removeSingleMarkedLot = (lotIdToRemove) => {
		const { markedLots } = this.state
		const updatedMarks = markedLots.filter((mark) => mark.lotId !== lotIdToRemove)

		this.setState({ markedLots: updatedMarks })
		this.saveMarkedLotsToStorage(updatedMarks)

		toast.success("Lot removed from marked list", { autoClose: 2000 })
	}

	componentWillUnmount() {
		if (this.userMarker) {
			this.userMarker.remove()
		}
		if (this.map) {
			this.map.remove()
		}
		if (this.watchId) {
			navigator.geolocation.clearWatch(this.watchId)
		}
	}

	loadPropertyCenterAndInitialize = async () => {
		const propertyCenter = await this.getPropertyCenter()
		this.initializeMap(propertyCenter)
		this.getUserLocation()
	}

	getPropertyCenter = async () => {
		try {
			// Get the active property ID (similar to lotStatus.js)
			const activeProperties = JSON.parse(localStorage.getItem("activeProperties")) || []
			if (activeProperties.length === 0) {
				console.warn("No active property selected")
				return [-96.797, 32.7767] // Default to Dallas
			}

			const propertyId = activeProperties[0]
			const userToken = localStorage.getItem("user")

			const response = await axios.post(
				`${settings.ep}/getGeo`,
				{ property: propertyId },
				{ headers: { Authorization: `Bearer ${userToken}` } }
			)

			if (response.data.status === 200 && response.data.geo && response.data.geo.features) {
				// Calculate center from all features
				const bounds = new mapboxgl.LngLatBounds()

				response.data.geo.features.forEach((feature) => {
					if (feature.geometry && feature.geometry.coordinates) {
						// Handle polygon coordinates
						if (feature.geometry.type === "Polygon") {
							feature.geometry.coordinates[0].forEach((coord) => {
								bounds.extend(coord)
							})
						}
					}
				})

				const center = bounds.getCenter()
				const propertyCenter = [center.lng, center.lat]
				this.setState({ propertyCenter })
				return propertyCenter
			}
		} catch (error) {
			console.error("Error loading property center:", error)
		}

		// Fallback to Dallas if property center can't be loaded
		return [-96.797, 32.7767]
	}

	initializeMap = (center) => {
		this.map = new mapboxgl.Map({
			container: this.mapContainer.current,
			style: "mapbox://styles/mapbox/satellite-streets-v12",
			center: center,
			zoom: 16,
			pitch: 0,
			bearing: 0,
		})

		this.map.on("load", () => {
			this.loadLotData()
			this.setupMapInteractions()
			this.setupUserTrackingListeners()
		})
	}

	getUserLocation = () => {
		if (navigator.geolocation) {
			navigator.geolocation.getCurrentPosition(
				(position) => {
					const { latitude, longitude } = position.coords
					const userLocation = [longitude, latitude]

					this.setState({ userLocation })
					this.map.flyTo({
						center: userLocation,
						zoom: 17,
					})

					this.addUserLocationMarker(userLocation)
					this.startLocationTracking()
				},
				(error) => {
					console.error("Error getting location:", error)
					toast.error("Unable to get your location. Please enable location services.", { autoClose: 2000 })
				},
				{
					enableHighAccuracy: true,
					timeout: 10000,
					maximumAge: 60000,
				}
			)
		} else {
			toast.error("Geolocation is not supported by this browser.", { autoClose: 2000 })
		}
	}

	startLocationTracking = () => {
		this.watchId = navigator.geolocation.watchPosition(
			(position) => {
				const { latitude, longitude } = position.coords
				const userLocation = [longitude, latitude]

				this.setState({ userLocation })
				this.updateUserLocationMarker(userLocation)
			},
			(error) => {
				console.error("Error tracking location:", error)
			},
			{
				enableHighAccuracy: true,
				timeout: 5000,
				maximumAge: 10000,
			}
		)
	}

	addUserLocationMarker = (location) => {
		// Remove existing marker if it exists
		if (this.userMarker) {
			this.userMarker.remove()
		}

		const el = document.createElement("div")
		el.className = "user-location-marker"
		el.style.width = "20px"
		el.style.height = "20px"
		el.style.backgroundColor = "#007cbf"
		el.style.border = "3px solid white"
		el.style.borderRadius = "50%"
		el.style.boxShadow = "0 0 10px rgba(0,0,0,0.3)"

		this.userMarker = new mapboxgl.Marker(el).setLngLat(location).addTo(this.map)
	}

	updateUserLocationMarker = (location) => {
		if (this.userMarker) {
			this.userMarker.setLngLat(location)
		}

		// Follow user if tracking is enabled
		if (this.state.isFollowingUser) {
			this.map.easeTo({
				center: location,
				duration: 1000,
			})
		}
	}

	setupUserTrackingListeners = () => {
		// Listen for user-initiated map movement
		this.map.on("dragstart", () => {
			if (this.state.isFollowingUser) {
				this.setState({
					isFollowingUser: false,
					showStickToMeButton: true,
				})
			}
		})
	}

	handleStickToMe = () => {
		const { userLocation } = this.state

		this.setState({
			isFollowingUser: true,
			showStickToMeButton: false,
		})

		if (userLocation) {
			this.map.flyTo({
				center: userLocation,
				zoom: 16.5,
				duration: 1500,
			})
		}
	}

	loadLotData = async () => {
		try {
			// Get the active property ID
			const activeProperties = JSON.parse(localStorage.getItem("activeProperties")) || []
			if (activeProperties.length === 0) {
				toast.error("No active property selected", { autoClose: 2000 })
				return
			}

			const propertyId = activeProperties[0]
			const userToken = localStorage.getItem("user")

			// Use the same getGeo endpoint as lotStatus.js
			const response = await axios.post(
				`${settings.ep}/getGeo`,
				{ property: propertyId },
				{ headers: { Authorization: `Bearer ${userToken}` } }
			)

			if (response.data.status === 200 && response.data.geo && response.data.geo.features) {
				const lots = response.data.geo.features
				this.setState({ lots })

				if (Array.isArray(lots) && lots.length > 0) {
					this.addLotsToMap(lots)
				} else {
					console.warn("No lot data available")
					toast.warning("No lot data available for mapping", { autoClose: 2000 })
				}
			} else {
				throw new Error("Invalid response format")
			}
		} catch (error) {
			console.error("Error loading lot data:", error)
			toast.error("Failed to load lot data", { autoClose: 2000 })
		}
	}

	addLotsToMap = (lots) => {
		if (!lots || !lots.length) return

		// The lots from getGeo are already in GeoJSON format
		const validFeatures = lots.filter((feature) => feature.geometry && feature.properties && feature.properties.id)

		if (validFeatures.length === 0) {
			console.warn("No lots have valid geometry data")
			toast.warning("No lots have location data available", { autoClose: 2000 })
			return
		}

		const geojsonData = {
			type: "FeatureCollection",
			features: validFeatures,
		}

		try {
			// Remove existing source if it exists
			if (this.map.getSource("lots")) {
				this.map.removeLayer("lot-fills")
				this.map.removeLayer("lot-borders")
				this.map.removeSource("lots")
			}

			this.map.addSource("lots", {
				type: "geojson",
				data: geojsonData,
			})

			this.updateMapColors()
		} catch (error) {
			console.error("Error adding lots to map:", error)
			toast.error("Failed to display lots on map", { autoClose: 2000 })
		}
	}

	updateMapColors = () => {
		if (!this.map || !this.map.getSource("lots")) return

		const { statusMode } = this.state

		// Colors from lotStatus.js
		const colors = {
			builderNoTakedown: "#1aa1ff",
			takedown: "#805203",
			developer: "white",
			start: "#c1c810",
			complete: "#ee8723",
			closed: "#818181",
			model: "#7a0b9e",
			highlighted: "#C0FD05",
			// Sale status colors
			noSale: "#007cbf", // blue
			saleNoClosure: "#ff8c00", // orange
			saleClosed: "#9c9c9c",
		}

		let fillColor, fillOpacity

		if (statusMode === "construction") {
			fillColor = [
				"case",
				["==", ["get", "model"], 1],
				colors.model,
				["!=", ["get", "closing_date"], null],
				colors.closed,
				["all", ["!=", ["get", "builder"], null], ["==", ["get", "takedown_date"], null]],
				colors.builderNoTakedown,
				["!", ["has", "takedown_date"]],
				colors.developer,
				["!=", ["get", "complete_date"], null],
				colors.complete,
				["!=", ["get", "start_date"], null],
				colors.start,
				["!=", ["get", "takedown_date"], null],
				colors.takedown,
				"#FFF",
			]
			fillOpacity = ["case", ["==", ["get", "model"], 1], 0.9, ["!=", ["get", "closing_date"], null], 0.5, 0.9]
		} else {
			// Sale status mode
			fillColor = [
				"case",
				["!=", ["get", "closing_date"], null],
				colors.saleClosed, // green - has closing
				["!=", ["get", "sale_date"], null],
				colors.saleNoClosure, // orange - has sale but no closing
				colors.noSale, // blue - no sale
			]
			fillOpacity = 0.8
		}

		// Remove existing layers
		if (this.map.getLayer("lot-fills")) {
			this.map.removeLayer("lot-fills")
		}
		if (this.map.getLayer("lot-borders")) {
			this.map.removeLayer("lot-borders")
		}

		// Add lot polygons
		this.map.addLayer({
			id: "lot-fills",
			type: "fill",
			source: "lots",
			paint: {
				"fill-color": fillColor,
				"fill-opacity": fillOpacity,
			},
		})

		// Add lot borders
		this.map.addLayer({
			id: "lot-borders",
			type: "line",
			source: "lots",
			paint: {
				"line-color": "#EFEFEF",
				"line-width": 2,
			},
		})
	}

	setupMapInteractions = () => {
		this.map.on("click", "lot-fills", (e) => {
			const lot = e.features[0]
			this.handleLotClick(lot)
		})

		this.map.on("mouseenter", "lot-fills", () => {
			this.map.getCanvas().style.cursor = "pointer"
		})

		this.map.on("mouseleave", "lot-fills", () => {
			this.map.getCanvas().style.cursor = ""
		})
	}

	handleLotClick = (lot) => {
		const lotData = lot.properties
		this.setState({
			selectedLot: lotData,
			showLotForm: true,
			start_date: null,
			complete_date: null,
			showMarkingOptions: false,
		})
	}

	handleSaveLotData = async () => {
		const { selectedLot, start_date, complete_date } = this.state

		if (!start_date && !complete_date) {
			toast.error("Please enter at least one date", { autoClose: 2000 })
			return
		}

		this.setState({ loading: true })

		try {
			const userToken = localStorage.getItem("user")

			const payload = {
				lotId: selectedLot.id,
				start_date: start_date ? moment(start_date).unix() : null,
				complete_date: complete_date ? moment(complete_date).unix() : null,
				action: "driveModeSubmitLot",
			}

			let resp = await axios.post(`${settings.ep}/mainIngest`, payload, {
				headers: { Authorization: `Bearer ${userToken}` },
			})

			toast.success(resp.data.message, { autoClose: 2000 })

			// Reload lot data to get updated dates and refresh map colors
			await this.loadLotData()

			this.setState({
				showLotForm: false,
				selectedLot: null,
				start_date: null,
				complete_date: null,
			})
		} catch (error) {
			console.error("Error updating lot:", error)
			toast.error("Failed to update lot dates", { autoClose: 2000 })
		} finally {
			this.setState({ loading: false })
		}
	}

	handleCloseLotForm = () => {
		this.setState({
			showLotForm: false,
			selectedLot: null,
			start_date: null,
			complete_date: null,
			showMarkingOptions: false,
		})
	}

	handleMarkLot = (markType) => {
		const { selectedLot, markedLots } = this.state
		if (!selectedLot) return

		const newMark = {
			lotId: selectedLot.id,
			lotData: {
				address: selectedLot.address,
				lbs: `${selectedLot.lot}-${selectedLot.block}-${selectedLot.section_name}`,
				builder_name: selectedLot.builder_name,
			},
			markType,
			timestamp: new Date().toISOString(),
		}

		// Remove any existing mark for this lot
		const filteredMarks = markedLots.filter((mark) => mark.lotId !== selectedLot.id)
		const updatedMarks = [...filteredMarks, newMark]

		this.setState({ markedLots: updatedMarks })
		this.saveMarkedLotsToStorage(updatedMarks)

		//toast.success(`Lot marked as "${markType}"`, { autoClose: 2000 })
		this.handleCloseLotForm()
	}

	handleToggleStatusMode = () => {
		const newMode = this.state.statusMode === "construction" ? "sale" : "construction"
		this.setState({ statusMode: newMode }, () => {
			this.updateMapColors()
		})
	}

	handleEmailBuilders = () => {
		const { markedLots } = this.state

		if (markedLots.length === 0) {
			toast.warning("No lots marked for notification", { autoClose: 2000 })
			return
		}

		this.setState({ showEmailConfirmModal: true })
	}

	confirmEmailBuilders = async () => {
		const { markedLots } = this.state

		this.setState({ loading: true, showEmailConfirmModal: false })

		try {
			const activeProperties = JSON.parse(localStorage.getItem("activeProperties")) || []
			const userToken = localStorage.getItem("user")

			const payload = {
				property: activeProperties[0],
				markedLots: markedLots,
				timestamp: new Date().toISOString(),
				action: "driveModeNotifyBuilders",
			}

			let resp = await axios.post(`${settings.ep}/mainIngest`, payload, {
				headers: { Authorization: `Bearer ${userToken}` },
			})

			toast.success(`Sent ${markedLots.length} lot notifications to builders!`, { autoClose: 2000 })
			this.clearMarkedLots()
		} catch (error) {
			console.error("Error notifying builders:", error)
			toast.error("Failed to send notifications to builders", { autoClose: 2000 })
		} finally {
			this.setState({ loading: false })
		}
	}

	cancelEmailBuilders = () => {
		this.setState({ showEmailConfirmModal: false })
	}

	handleShowMarkingOptions = () => {
		this.setState({ showMarkingOptions: true })
	}

	handleToggleMarkedLotsPanel = () => {
		this.setState((prevState) => ({
			showMarkedLotsPanel: !prevState.showMarkedLotsPanel,
		}))
	}

	handleExit = () => {
		this.setState({ isAnimating: true, isVisible: false })

		// Wait for animation to complete before calling parent's onExit
		setTimeout(() => {
			this.props.onExit()
		}, 300) // Match animation duration
	}

	render() {
		const {
			selectedLot,
			showLotForm,
			start_date,
			complete_date,
			start_dateFocused,
			complete_dateFocused,
			loading,
			showMarkingOptions,
			markedLots,
			statusMode,
			showMarkedLotsPanel,
			isAnimating,
			isVisible,
			showEmailConfirmModal,
			showStickToMeButton,
		} = this.state

		// Debug log for submit button visibility
		console.log("Date states:", { start_date, complete_date, showSubmit: !!(start_date || complete_date) })

		return (
			<div
				className={`fixed top-0 left-0 w-screen h-screen z-[999] bg-white flex flex-col transition-all duration-300 ease-in-out ${
					isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"
				}`}>
				<div className="bg-gray-50 border-b border-gray-200 shadow-sm">
					<div className="flex justify-between items-center px-4 py-3 md:px-5 md:py-4">
						<h3 className="m-0 text-gray-800 text-base md:text-lg font-semibold flex items-center">
							<img src={settings.logo} className="w-6" />
							<span className="ml-2">Drive Mode</span>
						</h3>
						<div className="flex items-center gap-2 md:gap-3">
							<div className="flex border border-gray-300 rounded-lg overflow-hidden">
								<button
									className={`px-3 py-2 text-sm font-medium transition-colors ${
										statusMode === "construction" ? "bg-blue text-white" : "bg-white text-blue"
									}`}
									onClick={() => statusMode !== "construction" && this.handleToggleStatusMode()}>
									Construction
								</button>
								<button
									className={`px-3 py-2 text-sm font-medium transition-colors border-l border-gray-300 ${
										statusMode === "sale" ? "bg-blue text-white" : "bg-white text-blue"
									}`}
									onClick={() => statusMode !== "sale" && this.handleToggleStatusMode()}>
									Sale
								</button>
							</div>
							<button
								className="px-3 py-2 bg-white text-gray-800 text-sm font-medium rounded-md border border-gray-300"
								onClick={this.handleExit}
								disabled={isAnimating}>
								Exit
							</button>
						</div>
					</div>

					{/* Compact Legend */}
					<div className="px-4 pb-2 md:px-5">
						<div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
							{statusMode === "construction" ? (
								<>
									<div className="flex items-center gap-1">
										<div
											className="w-3 h-3 rounded-sm"
											style={{ backgroundColor: "#805203" }}></div>
										<span className="text-gray-600">Taken Down</span>
									</div>
									<div className="flex items-center gap-1">
										<div
											className="w-3 h-3 rounded-sm"
											style={{ backgroundColor: "#c1c810" }}></div>
										<span className="text-gray-600">Start</span>
									</div>
									<div className="flex items-center gap-1">
										<div
											className="w-3 h-3 rounded-sm"
											style={{ backgroundColor: "#ee8723" }}></div>
										<span className="text-gray-600">Complete</span>
									</div>
									<div className="flex items-center gap-1">
										<div
											className="w-3 h-3 rounded-sm"
											style={{ backgroundColor: "#818181" }}></div>
										<span className="text-gray-600">Closed</span>
									</div>
								</>
							) : (
								<>
									<div className="flex items-center gap-1">
										<div
											className="w-3 h-3 rounded-sm"
											style={{ backgroundColor: "#007cbf" }}></div>
										<span className="text-gray-600">No Sale</span>
									</div>
									<div className="flex items-center gap-1">
										<div
											className="w-3 h-3 rounded-sm"
											style={{ backgroundColor: "#ff8c00" }}></div>
										<span className="text-gray-600">Sale, No Closing</span>
									</div>
									<div className="flex items-center gap-1">
										<div
											className="w-3 h-3 rounded-sm"
											style={{ backgroundColor: "#9c9c9c" }}></div>
										<span className="text-gray-600">Closed</span>
									</div>
								</>
							)}
						</div>
					</div>
				</div>

				<div className="relative flex-1 w-full h-full">
					<div ref={this.mapContainer} className="w-full h-full" />

					{/* Stick To Me Button - Absolutely positioned on map */}
					{showStickToMeButton && (
						<button
							className="absolute top-4 left-4 z-10 p-2 bg-white text-blue text-sm font-bold rounded-lg border border-dashed border-blue shadow-lg"
							onClick={this.handleStickToMe}>
							📍 Stick To Me
						</button>
					)}
				</div>

				{showLotForm && selectedLot && (
					<div className="fixed bottom-0 left-0 right-0 z-[1001] flex items-end animate-[fadeIn_0.3s_ease-out]">
						<div className="bg-white w-full rounded-t-[20px] max-h-[80vh] overflow-y-auto animate-[slideUp_0.3s_ease-out]">
							<div className="flex justify-between items-center p-4 md:p-5 border-b border-gray-200">
								<div>
									<h4 className="m-0 text-gray-800 text-lg font-semibold">
										{`${selectedLot.address} - LBS: ${selectedLot.lot}-${selectedLot.block}-${selectedLot.section_name}`}
									</h4>
									<div className="text-sm text-gray-600 font-normal mt-1">
										{`${selectedLot.builder_name}`}
									</div>
								</div>
								<button
									className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors text-2xl leading-none"
									onClick={this.handleCloseLotForm}>
									×
								</button>
							</div>

							{!showMarkingOptions ? (
								<>
									<div className="p-4 md:p-5">
										{!selectedLot.start_date && (
											<div className="mb-4 md:mb-5">
												<label className="block mb-2 font-semibold text-gray-800 text-sm">
													Start Date
												</label>
												<div className="w-full">
													<SingleDatePicker
														date={start_date}
														onDateChange={(date) => this.setState({ start_date: date })}
														focused={start_dateFocused}
														onFocusChange={({ focused }) =>
															this.setState({ start_dateFocused: focused })
														}
														id="start-date-picker"
														placeholder="Select start date"
														numberOfMonths={1}
														isOutsideRange={() => false}
													/>
												</div>
											</div>
										)}

										{!selectedLot.complete_date && (
											<div className="mb-4 md:mb-5">
												<label className="block mb-2 font-semibold text-gray-800 text-sm">
													Complete Date
												</label>
												<div className="w-full">
													<SingleDatePicker
														date={complete_date}
														onDateChange={(date) => this.setState({ complete_date: date })}
														focused={complete_dateFocused}
														onFocusChange={({ focused }) =>
															this.setState({ complete_dateFocused: focused })
														}
														id="complete-date-picker"
														placeholder="Select complete date"
														numberOfMonths={1}
														isOutsideRange={() => false}
													/>
												</div>
											</div>
										)}
									</div>

									<div className="p-4 md:p-5 border-t border-gray-200 flex justify-end items-center gap-2 md:gap-3 flex-wrap">
										<button
											className="px-4 py-2 md:px-5 md:py-3 border border-amber-500 text-amber-600 text-sm font-semibold rounded-lg hover:bg-amber-50 transition-colors disabled:opacity-50"
											onClick={this.handleShowMarkingOptions}
											disabled={loading}>
											Mark Issue
										</button>
										<div className="flex gap-2 md:gap-3">
											<button
												className="px-4 py-2 md:px-5 md:py-3 bg-gray-500 text-white text-sm font-semibold rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50"
												onClick={this.handleCloseLotForm}
												disabled={loading}>
												Cancel
											</button>
											{(start_date || complete_date) && (
												<button
													className="px-4 py-2 md:px-5 md:py-3 bg-blue text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
													onClick={this.handleSaveLotData}
													disabled={loading}>
													{loading ? "Submitting..." : "Submit"}
												</button>
											)}
										</div>
									</div>
								</>
							) : (
								<>
									<div className="p-4 md:p-5">
										<h5 className="mb-4 text-lg font-semibold text-gray-800">
											Mark what's wrong with this lot:
										</h5>
										<div className="space-y-3">
											<button
												className="w-full py-3 border-2 border-red-500 text-red-600 font-medium rounded-lg hover:bg-red-50 transition-colors"
												onClick={() => this.handleMarkLot("Missing Start")}>
												Missing Start
											</button>
											<button
												className="w-full py-3 border-2 border-red-500 text-red-600 font-medium rounded-lg hover:bg-red-50 transition-colors"
												onClick={() => this.handleMarkLot("Missing Complete")}>
												Missing Complete
											</button>
											<button
												className="w-full py-3 border-2 border-red-500 text-red-600 font-medium rounded-lg hover:bg-red-50 transition-colors"
												onClick={() => this.handleMarkLot("Missing Sale")}>
												Missing Sale
											</button>
											<button
												className="w-full py-3 border-2 border-red-500 text-red-600 font-medium rounded-lg hover:bg-red-50 transition-colors"
												onClick={() => this.handleMarkLot("Missing Closing")}>
												Missing Closing
											</button>
										</div>
									</div>

									<div className="p-4 md:p-5 border-t border-gray-200 flex justify-start">
										<button
											className="px-4 py-2 bg-gray-500 text-white text-sm font-semibold rounded-lg hover:bg-gray-600 transition-colors"
											onClick={() => this.setState({ showMarkingOptions: false })}>
											Back
										</button>
									</div>
								</>
							)}
						</div>
					</div>
				)}
				{markedLots.length > 0 && (
					<div className="fixed bottom-4 right-4 bg-white border border-gray-300 rounded-lg shadow-lg max-w-sm w-full z-[1000]">
						{/* Compact header - always visible */}
						<div
							className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 transition-colors"
							onClick={this.handleToggleMarkedLotsPanel}>
							<div className="flex items-center gap-2">
								<span className="text-sm font-semibold text-gray-800">
									Marked Lots ({markedLots.length})
								</span>
								<span className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded-full">
									{markedLots.length} issue{markedLots.length !== 1 ? "s" : ""}
								</span>
							</div>
							<svg
								className={`w-4 h-4 text-gray-500 transition-transform ${
									showMarkedLotsPanel ? "rotate-180" : ""
								}`}
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
							</svg>
						</div>

						{/* Expandable content */}
						<div
							className={`overflow-hidden transition-all duration-300 ease-in-out ${
								showMarkedLotsPanel ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
							}`}>
							<div className="border-t border-gray-200">
								<div className="p-3 space-y-2 max-h-60 overflow-y-auto">
									{markedLots.map((mark, index) => (
										<div
											key={index}
											className="flex justify-between items-center p-2 bg-gray-50 rounded border">
											<div className="flex-1 min-w-0">
												<div className="text-sm font-medium text-gray-900 truncate">
													{mark.lotData?.address || `Lot ID: ${mark.lotId}`}
												</div>
												<div className="text-xs text-gray-500">
													({mark.lotData?.lbs || "Unknown location"}){" "}
													{mark.lotData?.builder_name}
												</div>
											</div>
											<div className="flex items-center gap-2">
												<span className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded-full whitespace-nowrap">
													{mark.markType}
												</span>
												<button
													className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors text-sm leading-none"
													onClick={() => this.removeSingleMarkedLot(mark.lotId)}
													title="Remove this lot">
													×
												</button>
											</div>
										</div>
									))}
								</div>
								<div className="p-3 border-t border-gray-200 space-y-2">
									<button
										className="w-full px-3 py-2 bg-amber-500 text-white text-sm font-medium rounded-md hover:bg-amber-600 transition-colors disabled:opacity-50"
										onClick={this.handleEmailBuilders}
										disabled={loading}>
										{loading ? "Sending..." : `Email Builders (${markedLots.length})`}
									</button>
									<button
										className="w-full px-3 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 transition-colors"
										onClick={this.clearMarkedLots}>
										Clear All Marks
									</button>
								</div>
							</div>
						</div>
					</div>
				)}

				{/* Email Builders Confirmation Modal */}
				{showEmailConfirmModal && (
					<div className="fixed inset-0 z-[1002] flex items-center justify-center bg-black bg-opacity-50">
						<div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
							<div className="p-6">
								<h3 className="text-lg font-semibold text-gray-900 mb-4">Confirm Email to Builders</h3>
								<p className="text-gray-600 mb-6">
									Are you sure you want to send notifications for {markedLots.length} marked lot
									{markedLots.length !== 1 ? "s" : ""} to the builders?
								</p>
								<div className="flex justify-end gap-3">
									<button
										className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
										onClick={this.cancelEmailBuilders}>
										Cancel
									</button>
									<button
										className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors"
										onClick={this.confirmEmailBuilders}>
										Send Emails
									</button>
								</div>
							</div>
						</div>
					</div>
				)}
			</div>
		)
	}
}

export default DriveMode
